import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaArrowLeft, FaPaperPlane, FaBell, FaTimes } from "react-icons/fa";
import { supabase } from '../data/supabaseClient';
import CustomButton9, { CustomButton1, CustomButton10, CustomButton11, CustomButton2, CustomButton3, CustomButton4, CustomButton5, CustomButton6, CustomButton7, CustomButton8, CustomInputBox2, ChatInput, CloseButton2 } from '../Templates';
import FlowEngine from './flow/FlowEngine';
import UserActionsDropdown from '../common/UserActionsDropdown';
import { format } from 'date-fns';
import { useChat } from './ChatContext';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import { useTranslation } from 'react-i18next';
import { sendNewMessageNotification } from '../Notifications/notificationService';
import './ChatV2.css';

// How many messages to load per page. The most recent page loads on open;
// older messages load on demand when the user scrolls to the top.
const MESSAGE_PAGE_SIZE = 50;

// Shared select for message rows (message + per-recipient read status).
const MESSAGE_SELECT = `
  id,
  conversation_id,
  user_id,
  text,
  is_system_message,
  created_at,
  read_statuses:message_read_status(
    user_id,
    is_delivered,
    delivered_at,
    is_read,
    read_at
  )
`;

// Map a raw message row to a display message (attach current-user flag + sender profile).
const mapMessageRow = (msg, user, participantsData) => {
  const participantData = participantsData?.find(p => p.user_id === msg.user_id);
  return {
    ...msg,
    is_from_current_user: msg.user_id === user?.id,
    profiles: participantData?.profiles || null
  };
};

const isOptimisticId = (id) => typeof id === 'string' && id.startsWith('temp-');

// Message status checkmark components. Sent bubbles are now brand green, so ticks
// use the dark on-primary ink (#17200a): faded = sent/delivered, solid = read.
const INK = '#17200a';
const SingleCheckGray = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" style={{ marginLeft: '4px', display: 'inline-block', verticalAlign: 'middle', opacity: 0.5 }}>
    <path d="M13 4L6 11L3 8" stroke={INK} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DoubleCheckGray = () => (
  <svg width="15" height="14" viewBox="0 0 15 16" style={{ marginLeft: '4px', display: 'inline-block', verticalAlign: 'middle', opacity: 0.5 }}>
    <path d="M11 4L4 11L1 8" stroke={INK} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 4L7 11L4 8" stroke={INK} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DoubleCheckBlue = () => (
  <svg width="15" height="14" viewBox="0 0 15 16" style={{ marginLeft: '5px', display: 'inline-block', verticalAlign: 'middle', opacity: 1 }}>
    <path d="M11 4L4 11L1 8" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 4L7 11L4 8" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Helper function to determine message status icon
const getMessageStatusIcon = (message, currentUserId) => {
  // Only show status on sent messages (not received)
  if (message.user_id !== currentUserId) {
    return null;
  }

  const statuses = message.read_statuses || [];

  // No recipients (shouldn't happen, but handle gracefully)
  if (statuses.length === 0) {
    return <SingleCheckGray />; // ✓ (sent)
  }

  const allDelivered = statuses.every(s => s.is_delivered);
  const anyRead = statuses.some(s => s.is_read);
  const allRead = statuses.every(s => s.is_read);

  // All recipients have read the message
  if (allRead) {
    return <DoubleCheckBlue />; // ✓✓ (blue - read)
  }

  // At least one recipient has read (partial read in group chat)
  if (anyRead) {
    return <DoubleCheckBlue />; // ✓✓ (blue - read)
  }

  // All recipients have received but not read
  if (allDelivered) {
    return <DoubleCheckGray />; // ✓✓ (gray - delivered)
  }

  // Message sent but not yet delivered
  return <SingleCheckGray />; // ✓ (gray - sent)
};

const ChatWindow = ({ conversationId, onBack, occurrenceContext }) => {
  const { t } = useTranslation(['common', 'chat']);
  const { toggleChat } = useChat();
  const { addToast } = useToast();
  const [messages, setMessages] = useState([]);
  const [flowEvents, setFlowEvents] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [otherParticipant, setOtherParticipant] = useState(null);
  const [conversationDetails, setConversationDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null); // Scroll container (for load-older + scroll anchoring)

  // Merge real messages with this conversation's flow events — a durable, symmetric
  // timeline (both participants see every event, incl. their own), rendered inline
  // as system entries and translated per reader from the event_type + actor name.
  const timeline = useMemo(() => {
    const nameFor = (actorId) => {
      if (!actorId) return '';
      if (currentUser && actorId === currentUser.id) return currentUser.username || '';
      return otherParticipant?.username || '';
    };
    const events = (flowEvents || []).map((ev) => ({
      id: `flow-${ev.id}`,
      __flowEvent: true,
      text: t(`flow.event_${ev.event_type}`, { name: nameFor(ev.actor_id) }),
      created_at: ev.created_at,
    }));
    const merged = (events.length === 0 ? [...messages] : [...messages, ...events]).sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    // Insert a day-divider marker whenever the calendar day changes.
    const out = [];
    let lastDay = null;
    for (const item of merged) {
      const key = new Date(item.created_at).toDateString();
      if (key !== lastDay) {
        out.push({ id: `day-${key}`, __dayDivider: true, created_at: item.created_at });
        lastDay = key;
      }
      out.push(item);
    }
    return out;
  }, [messages, flowEvents, currentUser, otherParticipant, t]);

  // Human day label for divider chips (Today / Yesterday / date).
  const dayLabel = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return t('today', { ns: 'chat' });
    if (d.toDateString() === yest.toDateString()) return t('yesterday', { ns: 'chat' });
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
  };

  // Load flow events for this conversation + subscribe to new ones (live timeline).
  useEffect(() => {
    if (!conversationId) return undefined;
    let cancelled = false;
    supabase
      .from('flow_events')
      .select('id, actor_id, event_type, entity, created_at')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (error) console.error('Error loading flow events:', error);
        if (!cancelled) setFlowEvents(data || []);
      });

    const channel = supabase
      .channel(`flow_events-${conversationId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'flow_events', filter: `conversation_id=eq.${conversationId}` },
        (payload) => {
          setFlowEvents((prev) => (prev.some((e) => e.id === payload.new.id) ? prev : [...prev, payload.new]));
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [conversationId]);
  const lastMessageIdRef = useRef(null); // Track last message id so prepends don't scroll to bottom
  const loadedMessageIdsRef = useRef([]); // Ids currently on screen (scopes read-status polling)
  const subscriptionRef = useRef(null); // Store subscription channel reference
  const userRef = useRef(null); // Store user data to prevent stale closures
  const participantsDataRef = useRef(null); // Store participants data to prevent stale closures
  const pollIntervalRef = useRef(null); // Store polling interval reference
  // State for occurrence details (for recurring posts)
  const [occurrenceDetails, setOccurrenceDetails] = useState(null);

  const { 
    otherUnreadMessages,
    clearOtherUnreadMessage 
  } = useChat();
  
  const { 
    addNotification,
    showConversations
  } = useChat();

  // Clear unread status when entering conversation
  useEffect(() => {
    if (conversationId) {
      clearOtherUnreadMessage(conversationId);
    }
  }, [conversationId, clearOtherUnreadMessage]);

  // Mark messages as read in database when conversation is loaded
  useEffect(() => {
    const markMessagesAsRead = async () => {
      if (!conversationId || !currentUser) return;

      try {

        // Get all message IDs in this conversation
        const { data: messages, error: messagesError } = await supabase
          .from('messages')
          .select('id')
          .eq('conversation_id', conversationId);

        if (messagesError) {
          console.error('Error fetching messages:', messagesError);
          return;
        }

        const messageIds = messages.map(m => m.id);

        // Update read status in message_read_status table
        const { error: updateError } = await supabase
          .from('message_read_status')
          .update({
            is_read: true,
            is_delivered: true,  // Ensure delivered is also true
            read_at: new Date().toISOString(),
            delivered_at: new Date().toISOString(),  // Backfill if somehow not set
            updated_at: new Date().toISOString()
          })
          .eq('user_id', currentUser.id)
          .in('message_id', messageIds)
          .eq('is_read', false);

        if (updateError) {
          console.error('❌ Error marking messages as read:', updateError);
        } else {
        }
      } catch (error) {
        console.error('❌ Error in markMessagesAsRead:', error);
      }
    };

    markMessagesAsRead();
  }, [conversationId, currentUser]);

  const handleBack = () => {
    // Clear unread status for current conversation
    clearOtherUnreadMessage(conversationId);
    
    if (onBack) {
      onBack();
    }
    showConversations();
  };
  
  // Handle retry on error
  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    // Re-fetch everything
    fetchUser();
  };

  // Get current user
  const fetchUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      setCurrentUser(user);

      // If user is retrieved successfully, fetch messages and participants
      if (user) {
        const cleanup = await fetchMessagesAndParticipants(user);
        return cleanup; // ✅ Return the cleanup function so useEffect can use it
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setError(t('chat.error_user_fetch'));
      setIsLoading(false);
    }
  };

  // Fetch messages and setup real-time subscription
  const fetchMessagesAndParticipants = async (user) => {
    if (!conversationId || !user) return;

    try {
      setIsLoading(true);
      setError(null);
      
      
      // Get conversation details
      const { data: convoData, error: convoError } = await supabase
        .from('conversations')
        .select(`
          id,
          title,
          posts_id,
          package_id
        `)
        .eq('id', conversationId)
        .single();

      if (convoError) {
        console.error('Error fetching conversation details:', convoError);
        throw convoError;
      }

      
      // Fetch related post data if needed
      if (convoData.posts_id) {
        const { data: postData, error: postError } = await supabase
          .from('posts')
          .select('*')
          .eq('id', convoData.posts_id)
          .single();
          
        if (!postError && postData) {
          convoData.posts = postData;
        }
      }

      // Fetch related package data if needed (owner + travel date for the flow engine)
      if (convoData.package_id) {
        const { data: pkgData, error: pkgError } = await supabase
          .from('packages')
          .select('*')
          .eq('id', convoData.package_id)
          .single();
        if (!pkgError && pkgData) {
          convoData.package = pkgData;
        }
      }

      setConversationDetails(convoData);

      // Get participants separately to avoid recursion issues
      const { data: participantsData, error: participantsError } = await supabase
        .from('conversation_participants')
        .select(`
          user_id,
          profiles:user_id (id, username, avatar_url)
        `)
        .eq('conversation_id', conversationId);

      if (participantsError) {
        console.error('Error fetching participants:', participantsError);
        throw participantsError;
      }


      // Update refs to prevent stale closures in subscription
      userRef.current = user;
      participantsDataRef.current = participantsData;

      // Find other participant
      const otherParticipantData = participantsData?.find(
        p => p.user_id !== user.id
      );

      if (otherParticipantData?.profiles) {
        setOtherParticipant(otherParticipantData.profiles);
      }

      // Get the most recent page of messages with read status. We query newest-first
      // with a limit, then reverse to chronological order for display. Older messages
      // load on demand when the user scrolls to the top (loadOlderMessages).
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select(MESSAGE_SELECT)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
        .limit(MESSAGE_PAGE_SIZE);

      if (messagesError) {
        console.error('Error fetching messages:', messagesError);
        throw messagesError;
      }

      // Reverse to chronological (oldest → newest) and attach profiles (avoids RLS recursion).
      const messagesWithProfiles = (messagesData || [])
        .slice()
        .reverse()
        .map(msg => mapMessageRow(msg, user, participantsData));

      setMessages(messagesWithProfiles);
      // If we got a full page there may be older messages to load.
      setHasMoreMessages((messagesData || []).length === MESSAGE_PAGE_SIZE);
      
      // Mark messages as read
      // We don't have is_read field yet, so let's update the conversation to mark it as read
      // in a different way
      if (otherUnreadMessages.has(conversationId)) {
        clearOtherUnreadMessage(conversationId);
      }
      
      // Clean up any existing subscription first
      if (subscriptionRef.current) {
        await supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }

      // Subscribe to new messages AND read status updates

      const messageHandler = async (payload) => {

        // Add profile information to the message (using refs to prevent stale closures)
        const isFromCurrentUser = payload.new.user_id === userRef.current?.id;
        const participantData = participantsDataRef.current?.find(p => p.user_id === payload.new.user_id);


        // If message is from other user, mark as delivered AND read immediately (chat is open!)
        if (!isFromCurrentUser && userRef.current?.id) {
          await supabase
            .from('message_read_status')
            .update({
              is_delivered: true,
              delivered_at: new Date().toISOString(),
              is_read: true,  // Also mark as read since chat is open!
              read_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('message_id', payload.new.id)
            .eq('user_id', userRef.current.id);
        }

        // Add message immediately with empty read_statuses
        setMessages(prev => {
          // Check if message already exists (prevent duplicates from optimistic UI)
          const messageExists = prev.some(msg => msg.id === payload.new.id);

          if (messageExists) {
            return prev;
          }

          // New message, add it immediately
          return [...prev, {
            ...payload.new,
            is_from_current_user: isFromCurrentUser,
            profiles: participantData?.profiles || null,
            read_statuses: [] // Will be populated by subsequent fetch
          }];
        });

        // Fetch read statuses immediately (trigger is fast enough)
        const { data: readStatuses } = await supabase
          .from('message_read_status')
          .select('user_id, is_delivered, delivered_at, is_read, read_at')
          .eq('message_id', payload.new.id);


        // Update message with read statuses
        if (readStatuses) {
          setMessages(prev => prev.map(msg =>
            msg.id === payload.new.id
              ? { ...msg, read_statuses: readStatuses }
              : msg
          ));
        }
      };


      const channel = supabase
        .channel(`messages:${conversationId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        }, messageHandler)
        .subscribe((status) => {

          if (status === 'SUBSCRIBED') {
          } else if (status === 'CHANNEL_ERROR') {
            console.error(`❌ Channel error for conversation: ${conversationId}`);
          } else if (status === 'TIMED_OUT') {
            console.error(`⏱️ Subscription timed out for conversation: ${conversationId}`);
          } else if (status === 'CLOSED') {
          }
        });

      // Store channel reference
      subscriptionRef.current = channel;

      // Set up polling for read status updates (every 3 seconds).
      // Scoped to the messages currently on screen (loadedMessageIdsRef) so long
      // conversations don't re-poll their entire history every 3s.
      pollIntervalRef.current = setInterval(async () => {
        const messageIds = loadedMessageIdsRef.current;
        if (!messageIds || messageIds.length === 0) {
          return;
        }

        // Fetch updated read statuses for the on-screen messages
        const { data: updatedStatuses, error } = await supabase
          .from('message_read_status')
          .select('message_id, user_id, is_delivered, delivered_at, is_read, read_at')
          .in('message_id', messageIds);

        if (error) {
          console.error('❌ Error fetching read statuses:', error);
          return;
        }


        // Group statuses by message_id
        const statusesByMessage = new Map();
        updatedStatuses?.forEach(status => {
          if (!statusesByMessage.has(status.message_id)) {
            statusesByMessage.set(status.message_id, []);
          }
          statusesByMessage.get(status.message_id).push({
            user_id: status.user_id,
            is_delivered: status.is_delivered,
            delivered_at: status.delivered_at,
            is_read: status.is_read,
            read_at: status.read_at
          });
        });

        // Update messages with new read statuses
        setMessages(prev => prev.map(msg => ({
          ...msg,
          read_statuses: statusesByMessage.get(msg.id) || msg.read_statuses || []
        })));
      }, 3000); // Poll every 3 seconds

      // Return cleanup function
      return async () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        if (subscriptionRef.current) {
          await supabase.removeChannel(subscriptionRef.current);
          subscriptionRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error fetching conversation data:', error);
      setError(t('chat.error_conversation_load'));
    } finally {
      setIsLoading(false);
    }
  };

  // Load the previous page of (older) messages and prepend them, preserving the
  // user's scroll position so the viewport doesn't jump.
  const loadOlderMessages = async () => {
    if (isLoadingOlder || !hasMoreMessages) return;

    // Oldest real (non-optimistic) message currently loaded is our cursor.
    const oldest = messages.find(m => !isOptimisticId(m.id));
    if (!oldest) return;

    const container = messagesContainerRef.current;
    const prevScrollHeight = container ? container.scrollHeight : 0;
    const prevScrollTop = container ? container.scrollTop : 0;

    setIsLoadingOlder(true);
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(MESSAGE_SELECT)
        .eq('conversation_id', conversationId)
        .lt('created_at', oldest.created_at)
        .order('created_at', { ascending: false })
        .limit(MESSAGE_PAGE_SIZE);

      if (error) {
        console.error('Error loading older messages:', error);
        return;
      }

      const older = (data || [])
        .slice()
        .reverse()
        .map(msg => mapMessageRow(msg, userRef.current, participantsDataRef.current));

      setHasMoreMessages((data || []).length === MESSAGE_PAGE_SIZE);

      if (older.length > 0) {
        setMessages(prev => {
          const existing = new Set(prev.map(m => m.id));
          const deduped = older.filter(m => !existing.has(m.id));
          return [...deduped, ...prev];
        });
        // Restore scroll position after the prepended rows render.
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop =
              prevScrollTop + (messagesContainerRef.current.scrollHeight - prevScrollHeight);
          }
        });
      }
    } finally {
      setIsLoadingOlder(false);
    }
  };

  // Trigger older-message loading when the user scrolls near the top.
  const handleMessagesScroll = (e) => {
    if (e.target.scrollTop <= 80 && hasMoreMessages && !isLoadingOlder) {
      loadOlderMessages();
    }
  };

  // Keep the on-screen id list in sync (scopes the read-status poll above).
  useEffect(() => {
    loadedMessageIdsRef.current = messages
      .map(m => m.id)
      .filter(id => !isOptimisticId(id));
  }, [messages]);

  // Initial load
  useEffect(() => {
    let cleanupFn = null;
    let isMounted = true;
    // Reset paging/scroll state when switching conversations (component is reused).
    lastMessageIdRef.current = null;
    setHasMoreMessages(false);
    setIsLoadingOlder(false);

    // Initial fetch user and data
    const initialize = async () => {
      try {
        const cleanup = await fetchUser();

        // Only store cleanup if component is still mounted
        if (isMounted && typeof cleanup === 'function') {
          cleanupFn = cleanup;
        }
      } catch (error) {
        console.error('Error in initialize:', error);
      }
    };

    initialize();

    // Cleanup function - will be called when conversation changes or component unmounts
    return () => {
      isMounted = false;

      if (cleanupFn && typeof cleanupFn === 'function') {
        cleanupFn();
      }
    };
  }, [conversationId]);

  // Fetch occurrence details if occurrence context is provided
  useEffect(() => {
    const fetchOccurrenceDetails = async () => {
      if (!occurrenceContext?.occurrenceId) {
        setOccurrenceDetails(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('post_occurrences')
          .select('id, occurrence_date, occurrence_time, parent_post_id')
          .eq('id', occurrenceContext.occurrenceId)
          .single();

        if (error) throw error;

        setOccurrenceDetails(data);
      } catch (error) {
        console.error('Error fetching occurrence details:', error);
        setOccurrenceDetails(null);
      }
    };

    fetchOccurrenceDetails();
  }, [occurrenceContext]);

  // Scroll to bottom only when a NEW message is appended at the end — not when
  // older messages are prepended (load-older) or read-status updates re-render.
  useEffect(() => {
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    if (lastId !== lastMessageIdRef.current) {
      const isFirstLoad = lastMessageIdRef.current === null;
      lastMessageIdRef.current = lastId;
      messagesEndRef.current?.scrollIntoView({ behavior: isFirstLoad ? 'auto' : 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    // Support both form submission and direct call
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!newMessage.trim() || !conversationId || !currentUser) return;

    setIsSubmitting(true);

    // Save message text at the beginning so it's available in catch block
    const messageText = newMessage.trim();

    try {
      // Get all participants of the conversation
      const { data: participants, error: participantsError } = await supabase
        .from('conversation_participants')
        .select('user_id')
        .eq('conversation_id', conversationId);

      if (participantsError) throw participantsError;

      // For each participant, ensure conversation is visible
      for (const participant of participants) {
        const { data: visibilityRecord } = await supabase
          .from('conversation_visibility')
          .select('*')
          .eq('conversation_id', conversationId)
          .eq('user_id', participant.user_id)
          .maybeSingle();

        if (visibilityRecord?.status === 'hidden') {
          // Update visibility to visible
          await supabase
            .from('conversation_visibility')
            .update({ 
              status: 'visible',
              updated_at: new Date().toISOString() 
            })
            .eq('conversation_id', conversationId)
            .eq('user_id', participant.user_id);
        }
      }

      // Create optimistic message for immediate UI update
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        conversation_id: conversationId,
        user_id: currentUser.id,
        text: newMessage.trim(),
        is_read: false,
        created_at: new Date().toISOString(),
        is_from_current_user: true,
        profiles: {
          id: currentUser.id,
          username: currentUser.user_metadata?.username || currentUser.email?.split('@')[0],
          avatar_url: currentUser.user_metadata?.avatar_url || null
        },
        isOptimistic: true // Mark as optimistic so we can replace it later
      };

      // Add message to UI immediately
      setMessages(prev => [...prev, optimisticMessage]);

      // Clear input immediately for better UX
      setNewMessage('');

      // Insert new message with is_read set to false for recipients
      const { data: insertedMessage, error: messageError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          user_id: currentUser.id,
          text: messageText,
          is_read: false,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (messageError) throw messageError;

      // Replace optimistic message with real one from server
      if (insertedMessage) {
        setMessages(prev => prev.map(msg =>
          msg.id === optimisticMessage.id
            ? { ...insertedMessage, is_from_current_user: true, profiles: optimisticMessage.profiles }
            : msg
        ));
      }

      // Update conversation metadata
      const { error: updateError } = await supabase
        .from('conversations')
        .update({
          last_message: messageText,
          last_updated: new Date().toISOString()
        })
        .eq('id', conversationId);

      if (updateError) throw updateError;

      // Send notification to other participant
      if (otherParticipant && otherParticipant.id !== currentUser.id) {
        try {
          // Get sender's name from profile
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('full_name, username')
            .eq('id', currentUser.id)
            .single();

          const senderName = senderProfile?.full_name ||
                            senderProfile?.username ||
                            currentUser?.user_metadata?.full_name ||
                            currentUser?.user_metadata?.name ||
                            'Someone';

          const notificationResult = await sendNewMessageNotification(
            conversationId,
            currentUser.id,
            otherParticipant.id,
            senderName,
            messageText
          );

          if (!notificationResult.success) {
            console.warn('Failed to send message notification:', notificationResult.error);
          }
        } catch (notificationError) {
          console.error('Error sending message notification:', notificationError);
          // Don't fail message sending if notification fails
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message and show error state
      setMessages(prev => prev.map(msg =>
        msg.id?.startsWith('temp-') && msg.isOptimistic
          ? { ...msg, isErrorState: true, isOptimistic: false }
          : msg
      ));
      // Restore the message text so user can retry
      setNewMessage(messageText || newMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // If there's an error, show error message with retry option
  if (error) {
    return (
      <div className="chat-window-error">
        <p>{error}</p>
        <CustomButton2
          onClick={handleRetry}
          label={t('buttons.retry')}
        />
      </div>
    );
  }

  return (
    <div className="chat-window">
      {/* Fixed Header */}
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-back-button-container">
            <CustomButton8 onClick={handleBack}>
              <FaArrowLeft />
              {otherUnreadMessages.size > 0 && (
                <div className="notification-dot pulse"></div>
              )}
            </CustomButton8>
          </div>

          {otherParticipant && (
            <div className="participant-info-chat">
              {otherParticipant.avatar_url && (
                <img
                  src={otherParticipant.avatar_url}
                  alt={t('chat.profile_picture')}
                  className="avatar-img-chat"
                />
              )}
              <div className="participant-name">
                <p className="username">{otherParticipant.username || t('chat.unknown_user')}</p>
              </div>
            </div>
          )}
          
          <div className="actions-container">
            {/* Ride/package action flow moved to the pinned FlowStatusBar under the header */}

            {/* User Actions Dropdown for reporting */}
            {otherParticipant && otherParticipant.id !== currentUser?.id && (
              <UserActionsDropdown
                userId={otherParticipant.id}
                userName={otherParticipant.username || 'User'}
                context={{
                  conversation_id: conversationId,
                  post_id: conversationDetails?.posts_id || null,
                  package_id: conversationDetails?.package_id || null
                }}
                size="medium"
                variant="minimal"
              />
            )}
            
            {/* Close Chat Button */}
            <CloseButton2
              size="small"
              onClick={toggleChat}
              className="chat-close-button"
            />
          </div>
        </div>
      </div>

      {/* Ride / package action flow — pinned status bar under the header */}
      {(conversationDetails?.posts_id || conversationDetails?.package_id) && currentUser && (
        <FlowEngine
          entity={conversationDetails.posts_id ? 'ride' : 'package'}
          conversationId={conversationId}
          ownerId={
            conversationDetails.posts_id
              ? conversationDetails.posts?.user_id
              : conversationDetails.package?.user_id
          }
          travelDate={
            conversationDetails.posts_id
              ? conversationDetails.posts?.travel_date
              : conversationDetails.package?.travel_date
          }
          userId={currentUser.id}
          onError={(e) => {
            console.error('Flow action error:', e);
            // Flow failures were previously console-only (invisible to users) — surface them.
            addToast({
              type: 'error',
              title: t('flow.action_error_title'),
              description: e?.error === 'stale'
                ? t('flow.action_error_stale')
                : e?.error === 'full'
                  ? t('flow.action_error_full')
                  : t('flow.action_error_generic'),
            });
          }}
        />
      )}

      {/* Scrollable Messages Area */}
      <div className="chat-messages" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
        {isLoading ? (
          <div className="chat-messages-loading">
            <Spinner size="medium" customText={t('chat.loading')} />
          </div>
        ) : (
          <>
            {messages.length === 0 ? (
              <div className="no-messages">
                <p>{t('chat.no_messages')}</p>
              </div>
            ) : (
              <>
                {hasMoreMessages && (
                  <div className="chat-load-older">
                    {isLoadingOlder ? (
                      <Spinner size="small" showText={false} />
                    ) : (
                      <button
                        type="button"
                        className="chat-load-older-btn"
                        onClick={loadOlderMessages}
                      >
                        {t('chat.load_older')}
                      </button>
                    )}
                  </div>
                )}
                {timeline.map(message => {
                  // Day-divider chip (centered date separator).
                  if (message.__dayDivider) {
                    return (
                      <div key={message.id} className="chat-day-divider">
                        <span>{dayLabel(message.created_at)}</span>
                      </div>
                    );
                  }
                  // Inline flow event (derived from a flow notification) or a
                  // stored system message — both render in the system style.
                  if (message.__flowEvent || message.is_system_message || message.user_id === 'system') {
                    return (
                      <div key={message.id} className={`message system-message${message.__flowEvent ? ' flow-event' : ''}`}>
                        <p>{message.text}</p>
                        <span className="timestamp">
                          {format(new Date(message.created_at), 'HH:mm')}
                        </span>
                      </div>
                    );
                  }
                  
                  // Regular user message - use is_from_current_user flag instead of comparing IDs
                  const isCurrentUser = message.is_from_current_user || message.user_id === currentUser?.id;
                  
                  return (
                    <div
                      key={message.id}
                      className={`message ${isCurrentUser ? 'sent' : 'received'} ${message.isErrorState ? 'error-state' : ''}`}
                    >
                      <p>{message.text}</p>
                      <span className="timestamp">
                        {message.isErrorState ? (
                          <span className="error-indicator">{t('chat.failed_to_send')}</span>
                        ) : (
                          <>
                            {format(new Date(message.created_at), 'HH:mm')}
                            {getMessageStatusIcon(message, currentUser?.id)}
                          </>
                        )}
                      </span>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="chat-footer">
        <ChatInput
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onSend={handleSendMessage}
          placeholder={t('chat.type_message')}
          disabled={isSubmitting}
          maxLength={2000}
          sendLabel={t('chat.send_message')}
        />
      </div>
    </div>
  );
};

export default ChatWindow;